import { 
  deriveUserId, 
  deriveKeyFromPassword, 
  encryptData, 
  decryptData 
} from '../utils/CryptoHelpers';
import { v4 as uuidv4 } from 'uuid';
import { loadLocalKeys } from './KeyStorageManager';
import { loadContacts } from './ContactStorage';

/**
 * Initializes a new Unlink user account based on the provided password.
 * 
 * This function performs the following actions:
 * 1. Derives a user ID from the password using a stable hash function.
 * 2. Prevents overwriting an existing account by checking for a stored salt.
 * 3. Generates a per-user random salt for secure key derivation (PBKDF2).
 * 4. Creates a unique account fingerprint (used for client identification).
 * 5. Derives a symmetric key from the password + salt.
 * 6. Encrypts an empty contact list with the derived key.
 * 7. Stores the salt, fingerprint, and encrypted contact list in localStorage under keys tied to the user ID.
 * 
 * @param {string} password - The user-supplied password for this new account.
 * @returns {Promise<{ userId: string, salt: Uint8Array, fingerprint: string }>} 
 *          The derived user ID, raw salt, and generated fingerprint.
 * @throws {Error} If an account already exists for the given password.
 */
export async function initializeAccount(password) {
  // Derive a deterministic user ID from the password
  const userId = await deriveUserId(password);
  
  // Prevent account overwriting: if salt exists for this userId, account already exists
  if (localStorage.getItem(`unlink_${userId}_salt`)) {
    throw new Error("Account already exists for this password.");
  }

  // Generate a new 16-byte salt for PBKDF2 key derivation
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const base64Salt = btoa(String.fromCharCode(...salt));

  // Generate a unique fingerprint ID to associate with this account (not cryptographically secure)
  const fingerprint = crypto.randomUUID();

  // Persist the salt and fingerprint, tied to the user ID
  localStorage.setItem(`unlink_${userId}_salt`, base64Salt);
  localStorage.setItem(`unlink_${userId}_fingerprint`, fingerprint);

  // Derive a symmetric encryption key using the password + salt via PBKDF2
  const key = await deriveKeyFromPassword(password, salt);
  
  // Encrypt an empty contact list with the derived key
  const encrypted = await encryptData(JSON.stringify([]), key);

  // Store the encrypted empty contact list under the user ID
  localStorage.setItem(`unlink_${userId}_contacts`, JSON.stringify(encrypted));

  // Return identifiers for potential use elsewhere
  return { userId, salt, fingerprint };
}

export async function handleLogin(
  password,
  setPassword,
  setUserId,
  setSalt,
  setContacts,
  navigate,
) {
  const userId = await deriveUserId(password);

  // Check if this is a wipe password
  const linkedUserId = localStorage.getItem(`unlink_${userId}_wipe`);
  if (linkedUserId) {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`unlink_${linkedUserId}_`)) {
        localStorage.removeItem(key);
      }
    });

    // Also remove the wipe password entry itself
    localStorage.removeItem(`unlink_${userId}_wipe`);

    navigate("data_wiped");
    return false;
  }

  // Proceed with normal login
  const base64Salt = localStorage.getItem(`unlink_${userId}_salt`);
  if (!base64Salt) return false;

  try {
    const salt = new Uint8Array(atob(base64Salt).split("").map(c => c.charCodeAt(0)));
    const contacts = await loadContacts({userId, password});

    setContacts(contacts);
    setPassword(password);
    setUserId(userId);
    setSalt(salt);

    const { 
      kyberPrivateKey,
      kyberPublicKey,
      viewPrivateKey,
      viewPublicKey,
      fundingPrivateKey,
      fundingPublicKey,
      mnemonic,
    } = await loadLocalKeys({ userId, password, salt });

    if (!kyberPrivateKey || !kyberPublicKey || !viewPrivateKey || !viewPublicKey || !fundingPrivateKey || !fundingPublicKey || !mnemonic) {
      navigate("setup_unlink_secret");
    } else {
      navigate("contacts");
    }

    return true;
  } catch (e) {
    console.error("Login failed:", e);
    return false;
  }
}

export function handleLogout({setPassword, setUserId, setContacts, navigate}) {
  setPassword("");
  setUserId(null);
  setContacts([]);
  navigate("login");
}